import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getEmailAccountStatus } from "../utils";
import "./AccountStatus.css";

const REJECTED_STATUSES = new Set(["REJECTED", "ACCOUNT_CREATION_REJECTED"]);
const APPROVED_STATUSES = new Set(["APPROVED", "COMPLETED"]);

const parseTime = (value) => {
  if (!value) return 0;
  const time = Date.parse(value);
  return Number.isNaN(time) ? 0 : time;
};

const getRecordTime = (record) => {
  if (!record) return 0;
  return (
    parseTime(record.updated_at) ||
    parseTime(record.rejected_at) ||
    parseTime(record.approved_at) ||
    parseTime(record.created_at)
  );
};

const pickLatestRejectedRecord = (records = []) => {
  const rejectedRecords = records.filter((record) =>
    REJECTED_STATUSES.has(String(record?.request_status || record?.status || "").toUpperCase())
  );

  if (!rejectedRecords.length) return null;

  return rejectedRecords.sort((a, b) => getRecordTime(b) - getRecordTime(a))[0];
};

const hasApprovedRecord = (records = []) =>
  records.some((record) =>
    APPROVED_STATUSES.has(String(record?.request_status || record?.status || "").toUpperCase())
  );

const formatCompany = (value) => String(value || "").split("-").join(" ");

const RejectionDashboard = ({ loggedInUser, onSignOut }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const initialRejectedAccount = useMemo(() => location.state?.rejectedAccount || null, [location.state]);

  const [loading, setLoading] = useState(!initialRejectedAccount);
  const [error, setError] = useState(null);
  const [rejectedAccount, setRejectedAccount] = useState(() => initialRejectedAccount);

  const rejectionReason = useMemo(() => {
    const reason = rejectedAccount?.approval_reason || rejectedAccount?.rejection_reason || "";
    return String(reason || "").trim();
  }, [rejectedAccount]);

  const refresh = useCallback(async () => {
    if (!loggedInUser) return;

    setLoading(true);
    setError(null);

    try {
      const records = await getEmailAccountStatus(loggedInUser);

      if (!records || !Array.isArray(records)) {
        setRejectedAccount(null);
        setError("No account records found.");
        return;
      }

      if (hasApprovedRecord(records)) {
        navigate("/", { replace: true });
        return;
      }

      const latestRejected = pickLatestRejectedRecord(records);
      if (!latestRejected) {
        // If we already have a rejected account record (e.g. via navigation state),
        // do NOT bounce the user back to the default dashboard on transient empty
        // results (can happen during auth handoff / token refresh).
        if (!rejectedAccount) {
          setError("Rejection details are not available yet. Please refresh.");
        }
        return;
      }

      setRejectedAccount(latestRejected);
    } catch (err) {
      if (err?.name === 'AuthSessionInvalidError') {
        onSignOut?.();
        return;
      }
      console.error("Failed to load rejected account status", err);
      if (!rejectedAccount) {
        setError("Failed to load rejection details.");
      }
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loggedInUser, navigate, rejectedAccount]);

  useEffect(() => {
    // If we already have the rejected record from the redirect state, avoid a
    // second status fetch on first render (reduces flicker + avoids extra calls
    // during auth warm-up).
    if (initialRejectedAccount) {
      setRejectedAccount(initialRejectedAccount);
      setLoading(false);
      return;
    }

    refresh();
  }, [refresh, initialRejectedAccount]);

  if (loading) {
    return (
      <div className="status-container">
        <div className="status-card">
          <div className="loading-spinner"></div>
          <p style={{ textAlign: "center", marginTop: "1rem" }}>Loading your request status…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="status-container">
        <div className="status-card error">
          <h2>⚠️ Unable to Load Status</h2>
          <p>{error}</p>
          <button className="back-button" onClick={refresh}>
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const rejectedAt = rejectedAccount?.rejected_at
    ? new Date(rejectedAccount.rejected_at).toLocaleString()
    : null;

  const submittedAt = rejectedAccount?.created_at
    ? new Date(rejectedAccount.created_at).toLocaleString()
    : null;

  return (
    <div className="status-container">
      <div className="status-card">
        <h1>Account Request Status</h1>

        {/* Rejection banner — mirrors the completion "success-banner" but in red */}
        <div className="completion-section">
          <div className="success-banner rejection-banner">
            <h2>❌ Account Request Rejected</h2>
            <p>Your account request has been reviewed and was not approved.</p>
          </div>
        </div>

        {/* Request details — mirrors completion dashboard's "dashboard-info-section" */}
        <div className="dashboard-info-section rejection-info-section">
          <h3>Request Details</h3>
          <div className="dashboard-details">
            {rejectedAccount?.company_name && (
              <div className="dashboard-item">
                <span className="dashboard-label">Company Name</span>
                <span className="dashboard-value">
                  {formatCompany(rejectedAccount.company_name)}
                </span>
              </div>
            )}

            {rejectedAccount?.account_email && (
              <div className="dashboard-item">
                <span className="dashboard-label">Account Email</span>
                <span className="dashboard-value">{rejectedAccount.account_email}</span>
              </div>
            )}

            <div className="dashboard-item">
              <span className="dashboard-label">Status</span>
              <span
                className="dashboard-value"
                style={{ color: "#c0392b", fontWeight: 700 }}
              >
                Rejected
              </span>
            </div>

            <div className="dashboard-item">
              <span className="dashboard-label">Reason for Rejection</span>
              <span className="dashboard-value">
                {rejectionReason || "No reason provided."}
              </span>
            </div>

            {rejectedAt && (
              <div className="dashboard-item">
                <span className="dashboard-label">Rejected At</span>
                <span className="dashboard-value">{rejectedAt}</span>
              </div>
            )}

            {submittedAt && (
              <div className="dashboard-item">
                <span className="dashboard-label">Submitted At</span>
                <span className="dashboard-value">{submittedAt}</span>
              </div>
            )}
          </div>
        </div>

        {/* Support section — mirrors completion dashboard's "closure-summary" */}
        <div className="closure-summary">
          <h3 style={{ paddingLeft: 0 }}>Need Help?</h3>
          <div className="summary-details">
            <div className="summary-item">
              <span className="summary-icon" style={{ background: "#e74c3c" }}>?</span>
              <span>
                Contact support at{" "}
                <strong>800-985-1351</strong> or{" "}
                <strong>support@remotetek.co</strong>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RejectionDashboard;
